import {
  faExclamationCircle,
  faPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { Link, NavLink } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import NoProducts from 'src/assets/no-products.svg';
import NoResults from 'src/assets/no-results.svg';
import Button from 'src/components/ui/Button/Button';
import EmptyState from 'src/components/ui/EmptyState/EmptyState';
import PageHeader from 'src/components/ui/PageHeader/PageHeader';
import ProductItem from 'src/components/ui/ProductItem/ProductItem';
import { TableQueryParameters } from 'src/components/ui/Table/Table.interface';
import TablePagination from 'src/components/ui/Table/TablePagination/TablePagination';
import TableSearch from 'src/components/ui/Table/TableSearch/TableSearch';
import { paginationDefaultState } from 'src/constants/paginationConstants';
import { routes } from 'src/routes';
import {
  getProductList,
  setProductList,
} from 'src/store/actions/productActions';
import { Role } from 'src/store/models/Auth';
import { useLoadingSelector } from 'src/utils/useLoadingSelector';
import { useQueryParams } from 'src/utils/useQueryParams';

import classes from './Products.module.scss';

const Products: FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const user = useAppSelector((state) => state.auth.user);
  const productList = useAppSelector((state) => state.product.productList);
  const loading = useLoadingSelector({ actionType: getProductList.toString() });
  const isManufacturer = user?.role === Role.MANUFACTURER;

  const { urlParams } = useQueryParams<TableQueryParameters>(
    location.search,
    paginationDefaultState
  );

  useEffect(() => {
    dispatch(setProductList({ count: 0, data: [], ctx: null }));
    return () => {
      dispatch(setProductList({ count: 0, data: [], ctx: null }));
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    dispatch(
      getProductList({ params: urlParams, mId: user?.manufacturer?.id })
    );
    // eslint-disable-next-line
  }, [location]);

  return (
    <div className={classes.Container}>
      <PageHeader
        title={t('common:products')}
        className={classes.Header}
        endNode={
          <div className={classes.HeaderRight}>
            {isManufacturer && (
              <aside>
                <NavLink to={routes.ADD_PRODUCT}>
                  <Button
                    endIcon={<FontAwesomeIcon icon={faPlus} />}
                    className={classes.HeaderButton}
                  >
                    {t('products:add_product')}
                  </Button>
                </NavLink>
              </aside>
            )}
            <section>
              <Link to={routes.ERROR_INDEX}>
                <Button
                  startIcon={<FontAwesomeIcon icon={faExclamationCircle} />}
                >
                  {t('products:to_error_index')}
                </Button>
              </Link>
              <TableSearch
                placeholder={t('common:search_field')}
                urlParams={urlParams}
              />
            </section>
          </div>
        }
      />
      <section>
        {!loading
          ? productList?.data?.map((product, i) => (
              <ProductItem
                key={product?.id}
                edit={isManufacturer}
                product={product}
              />
            ))
          : [...Array(4)].map((_, i) => (
              <ProductItem key={i} product={null} loading />
            ))}
      </section>
      {!productList?.data?.length && (
        <section>
          {!urlParams?.q ? (
            <EmptyState
              title={t('products:no_products')}
              subtitle={t('products:no_products_sub')}
              icon={NoProducts}
            />
          ) : (
            <EmptyState
              title={t('common:no_results')}
              subtitle={t('common:no_results_subtitle')}
              icon={NoResults}
            />
          )}
        </section>
      )}
      {productList?.count ? (
        <TablePagination
          className={classes.Pagination}
          urlParams={urlParams}
          total={productList?.count}
        />
      ) : null}
    </div>
  );
};

export default Products;
